import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  writeBatch,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  DocumentReference,
  collection as firestoreCollection
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';

export interface UserRating {
  userId: string;
  email: string;
  rating: number;
  timestamp: any; // Firestore timestamp
}

export interface ModelRatingStats {
  modelId: string;
  averageRating: number;
  totalRatings: number;
  lastUpdated: any; // Firestore timestamp
  userRating?: number; // Current user's rating if exists
}

export interface UserPromptRating {
  userId: string;
  email: string;
  rating: number;
  timestamp: any; // Firestore timestamp
  modelId: string;
  promptId: string;
}

export interface PromptRatingStats {
  modelId: string;
  promptId: string;
  averageRating: number;
  totalRatings: number;
  lastUpdated: any; // Firestore timestamp
  userRating?: number; // Current user's rating if exists
}

export interface ModelComment {
  modelId: string;
  comment: string;
  lastUpdated: any; // Firestore timestamp
  lastUpdatedBy: string; // Email of the user who last updated
}

@Injectable({
  providedIn: 'root'
})
export class RatingService {
  private readonly RATINGS_COLLECTION = 'ratings';
  private readonly PROMPT_RATINGS_COLLECTION = 'prompt-ratings';
  private readonly MODEL_COMMENTS_COLLECTION = 'model-comments';

  constructor(
    private firestore: Firestore,
    private auth: Auth
  ) {}

  /**
   * Get rating statistics for a model
   * @param modelId The ID of the model to get ratings for
   */
  getModelRatingStats(modelId: string): Observable<ModelRatingStats> {
    return new Observable(subscriber => {
      const userId = this.auth.currentUser?.uid;
      if (!userId) {
        subscriber.error('User not authenticated');
        return;
      }

      const modelRef = doc(this.firestore, `${this.RATINGS_COLLECTION}/${modelId}`);
      const userRatingRef = doc(modelRef, 'ratings', userId);

      // Get both the model stats and user's rating in parallel
      Promise.all([
        getDoc(modelRef),
        getDoc(userRatingRef)
      ]).then(([modelDoc, userRatingDoc]) => {
        const stats: ModelRatingStats = {
          modelId,
          averageRating: 0,
          totalRatings: 0,
          lastUpdated: null
        };

        if (modelDoc.exists()) {
          const data = modelDoc.data();
          stats.averageRating = data['averageRating'] || 0;
          stats.totalRatings = data['totalRatings'] || 0;
          stats.lastUpdated = data['lastUpdated'];
        }

        if (userRatingDoc.exists()) {
          stats.userRating = userRatingDoc.data()?.['rating'];
        }

        subscriber.next(stats);
        subscriber.complete();
      }).catch(error => {
        console.error('Error getting rating stats:', error);
        subscriber.error(error);
      });
    });
  }

  /**
   * Submit or update a rating for a model
   * @param modelId The ID of the model to rate
   * @param rating The rating value (1-10)
   */
  async rateModel(modelId: string, rating: number): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || !user.uid || !user.email) {
      throw new Error('User must be authenticated to rate models');
    }

    if (rating < 1 || rating > 10) {
      throw new Error('Rating must be between 1 and 10');
    }

    // Get references
    const modelRef = doc(this.firestore, `${this.RATINGS_COLLECTION}/${modelId}`);
    const userRatingRef = doc(modelRef, 'ratings', user.uid);

    // Start a batch to ensure atomic updates
    const batch = writeBatch(this.firestore);

    // 1. Set/update the user's rating
    const userRating: UserRating = {
      userId: user.uid,
      email: user.email,
      rating,
      timestamp: serverTimestamp()
    };
    batch.set(userRatingRef, userRating, { merge: true });

    // 2. Get current stats to calculate new average
    const modelDoc = await getDoc(modelRef);
    const currentStats = modelDoc.exists() ? modelDoc.data() : {
      averageRating: 0,
      totalRatings: 0
    };

    const currentTotal = currentStats['totalRatings'] || 0;
    const currentAverage = currentStats['averageRating'] || 0;

    // Check if this is an update to an existing rating
    const userRatingDoc = await getDoc(userRatingRef);
    const isUpdate = userRatingDoc.exists();

    const newTotal = isUpdate ? currentTotal : currentTotal + 1;
    let newSum = currentAverage * currentTotal;

    if (isUpdate) {
      const oldRating = userRatingDoc.data()?.['rating'] || 0;
      newSum = newSum - oldRating + rating;
    } else {
      newSum += rating;
    }

    const newAverage = newTotal > 0 ? newSum / newTotal : 0;

    // 3. Update the model's rating stats
    batch.set(modelRef, {
      averageRating: parseFloat(newAverage.toFixed(2)),
      totalRatings: newTotal,
      lastUpdated: serverTimestamp()
    }, { merge: true });

    // Commit the batch
    await batch.commit();
  }

  /**
   * Get the current user's rating for a model
   * @param modelId The ID of the model
   */
  async getUserRating(modelId: string): Promise<number | null> {
    const userId = this.auth.currentUser?.uid;
    if (!userId) return null;

    const userRatingRef = doc(
      this.firestore,
      `${this.RATINGS_COLLECTION}/${modelId}/ratings/${userId}`
    );

    const docSnap = await getDoc(userRatingRef);
    return docSnap.exists() ? docSnap.data()?.['rating'] : null;
  }

  /**
   * Get all models rated by the current user
   * @param minRating Optional minimum rating to filter by
   */
  async getRatedModels(minRating = 0): Promise<Array<{ modelId: string; rating: number }>> {
    const userId = this.auth.currentUser?.uid;
    if (!userId) return [];

    // Query all ratings subcollections where the user has rated
    const ratingsQuery = query(
      firestoreCollection(this.firestore, this.RATINGS_COLLECTION)
    );

    const querySnapshot = await getDocs(ratingsQuery);
    const results: Array<{ modelId: string; rating: number }> = [];

    for (const docRef of querySnapshot.docs) {
      const userRatingRef = doc(
        this.firestore,
        `${this.RATINGS_COLLECTION}/${docRef.id}/ratings/${userId}`
      );
      const ratingDoc = await getDoc(userRatingRef);

      if (ratingDoc.exists()) {
        const rating = ratingDoc.data()?.['rating'];
        if (rating >= minRating) {
          results.push({
            modelId: docRef.id,
            rating
          });
        }
      }
    }

    return results;
  }

  /**
   * Delete all ratings by the current user
   */
  /**
   * Delete all ratings by the current user
   */
  async deleteAllUserRatings(modelIds?: string[]): Promise<void> {
    const user = this.auth.currentUser;
    if (!user?.uid) {
      throw new Error('User must be authenticated to delete ratings');
    }

    const batch = writeBatch(this.firestore);

    if (modelIds?.length) {
      // Process only the specified models
      for (const modelId of modelIds) {
        await this.deleteRatingForModel(batch, modelId, user.uid);
      }
    } else {
      // If no modelIds provided, delete all user ratings
      const userRatings = await this.getRatedModels();
      if (userRatings.length === 0) return;

      for (const { modelId } of userRatings) {
        await this.deleteRatingForModel(batch, modelId, user.uid);
      }
    }

    return batch.commit();
  }

  /**
   * Delete a single rating for a model
   * @param modelId The ID of the model to delete rating for
   */
  async deleteRating(modelId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user?.uid) {
      throw new Error('User must be authenticated to delete rating');
    }

    const batch = writeBatch(this.firestore);
    await this.deleteRatingForModel(batch, modelId, user.uid);
    await batch.commit();
  }

  /**
   * Delete ratings sequentially for multiple models (one by one)
   * @param modelIds Array of model IDs to delete ratings for
   */
  async deleteRatingsSequentially(modelIds: string[]): Promise<void> {
    if (!modelIds || modelIds.length === 0) {
      return;
    }

    const user = this.auth.currentUser;
    if (!user?.uid) {
      throw new Error('User must be authenticated to delete ratings');
    }

    for (const modelId of modelIds) {
      try {
        await this.deleteRating(modelId);
        console.log(`Successfully deleted rating for model: ${modelId}`);
      } catch (error) {
        console.error(`Failed to delete rating for model ${modelId}:`, error);
        // Continue with next model even if one fails
      }
    }
  }

  /**
   * Helper method to delete a rating and update model stats
   */
  private async deleteRatingForModel(batch: any, modelId: string, userId: string): Promise<void> {
    const userRatingRef = doc(
      this.firestore,
      `${this.RATINGS_COLLECTION}/${modelId}/ratings/${userId}`
    );

    // Get the user's current rating first
    const userRatingDoc = await getDoc(userRatingRef);
    if (!userRatingDoc.exists()) return;

    const userRating = userRatingDoc.data()?.['rating'] || 0;

    // Delete the user's rating
    batch.delete(userRatingRef);

    // Get model reference to update its stats
    const modelRef = doc(this.firestore, `${this.RATINGS_COLLECTION}/${modelId}`);
    const modelDoc = await getDoc(modelRef);

    if (!modelDoc.exists()) return;

    const currentStats = modelDoc.data();
    const currentTotal = currentStats['totalRatings'] || 0;
    const currentAverage = currentStats['averageRating'] || 0;

    // If this was the only rating, delete the model document
    if (currentTotal <= 1) {
      batch.delete(modelRef);
      return;
    }

    // Recalculate average without the deleted rating
    const currentSum = currentAverage * currentTotal;
    const newTotal = currentTotal - 1;
    const newAverage = newTotal > 0 ? (currentSum - userRating) / newTotal : 0;

    // Update model stats
    batch.set(modelRef, {
      averageRating: parseFloat(newAverage.toFixed(2)),
      totalRatings: newTotal,
      lastUpdated: serverTimestamp()
    }, { merge: true });
  }

  // ===== NEW PROMPT RATING METHODS =====

  /**
   * Get rating statistics for a specific model-prompt combination
   * @param modelId The ID of the model
   * @param promptId The ID of the prompt
   */
  getPromptRatingStats(modelId: string, promptId: string): Observable<PromptRatingStats> {
    return new Observable(subscriber => {
      const userId = this.auth.currentUser?.uid;
      if (!userId) {
        subscriber.error('User not authenticated');
        return;
      }

      const promptRatingKey = `${modelId}-${promptId}`;
      const promptRef = doc(this.firestore, `${this.PROMPT_RATINGS_COLLECTION}/${promptRatingKey}`);
      const userRatingRef = doc(promptRef, 'ratings', userId);

      // Get both the prompt stats and user's rating in parallel
      Promise.all([
        getDoc(promptRef),
        getDoc(userRatingRef)
      ]).then(([promptDoc, userRatingDoc]) => {
        const stats: PromptRatingStats = {
          modelId,
          promptId,
          averageRating: 0,
          totalRatings: 0,
          lastUpdated: null
        };

        if (promptDoc.exists()) {
          const data = promptDoc.data();
          stats.averageRating = data['averageRating'] || 0;
          stats.totalRatings = data['totalRatings'] || 0;
          stats.lastUpdated = data['lastUpdated'];
        }

        if (userRatingDoc.exists()) {
          stats.userRating = userRatingDoc.data()?.['rating'];
        }

        subscriber.next(stats);
        subscriber.complete();
      }).catch(error => {
        console.error('Error getting prompt rating stats:', error);
        subscriber.error(error);
      });
    });
  }

  /**
   * Submit or update a rating for a model-prompt combination
   * @param modelId The ID of the model
   * @param promptId The ID of the prompt
   * @param rating The rating value (1-10)
   */
  async ratePrompt(modelId: string, promptId: string, rating: number): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || !user.uid || !user.email) {
      throw new Error('User must be authenticated to rate prompts');
    }

    if (rating < 1 || rating > 10) {
      throw new Error('Rating must be between 1 and 10');
    }

    const promptRatingKey = `${modelId}-${promptId}`;

    // Get references
    const promptRef = doc(this.firestore, `${this.PROMPT_RATINGS_COLLECTION}/${promptRatingKey}`);
    const userRatingRef = doc(promptRef, 'ratings', user.uid);

    // Start a batch to ensure atomic updates
    const batch = writeBatch(this.firestore);

    // 1. Set/update the user's rating
    const userRating: UserPromptRating = {
      userId: user.uid,
      email: user.email,
      rating,
      timestamp: serverTimestamp(),
      modelId,
      promptId
    };
    batch.set(userRatingRef, userRating, { merge: true });

    // 2. Get current stats to calculate new average
    const promptDoc = await getDoc(promptRef);
    const currentStats = promptDoc.exists() ? promptDoc.data() : {
      averageRating: 0,
      totalRatings: 0
    };

    const currentTotal = currentStats['totalRatings'] || 0;
    const currentAverage = currentStats['averageRating'] || 0;

    // Check if this is an update to an existing rating
    const userRatingDoc = await getDoc(userRatingRef);
    const isUpdate = userRatingDoc.exists();

    const newTotal = isUpdate ? currentTotal : currentTotal + 1;
    let newSum = currentAverage * currentTotal;

    if (isUpdate) {
      const oldRating = userRatingDoc.data()?.['rating'] || 0;
      newSum = newSum - oldRating + rating;
    } else {
      newSum += rating;
    }

    const newAverage = newTotal > 0 ? newSum / newTotal : 0;

    // 3. Update the prompt's rating stats
    batch.set(promptRef, {
      averageRating: parseFloat(newAverage.toFixed(2)),
      totalRatings: newTotal,
      lastUpdated: serverTimestamp(),
      modelId,
      promptId
    }, { merge: true });

    // Commit the batch
    await batch.commit();
  }

  /**
   * Get the current user's rating for a model-prompt combination
   * @param modelId The ID of the model
   * @param promptId The ID of the prompt
   */
  async getUserPromptRating(modelId: string, promptId: string): Promise<number | null> {
    const userId = this.auth.currentUser?.uid;
    if (!userId) return null;

    const promptRatingKey = `${modelId}-${promptId}`;
    const userRatingRef = doc(
      this.firestore,
      `${this.PROMPT_RATINGS_COLLECTION}/${promptRatingKey}/ratings/${userId}`
    );

    const docSnap = await getDoc(userRatingRef);
    return docSnap.exists() ? docSnap.data()?.['rating'] : null;
  }

  /**
   * Delete a single rating for a model-prompt combination
   * @param modelId The ID of the model
   * @param promptId The ID of the prompt
   */
  async deletePromptRating(modelId: string, promptId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user?.uid) {
      throw new Error('User must be authenticated to delete rating');
    }

    const batch = writeBatch(this.firestore);
    await this.deletePromptRatingForCombination(batch, modelId, promptId, user.uid);
    await batch.commit();
  }

  /**
   * Delete all prompt ratings by the current user
   * @param modelIds Optional array of model IDs to filter by
   * @param promptIds Optional array of prompt IDs to filter by
   */
  async deleteAllUserPromptRatings(modelIds?: string[], promptIds?: string[]): Promise<void> {
    const user = this.auth.currentUser;
    if (!user?.uid) {
      throw new Error('User must be authenticated to delete ratings');
    }

    const batch = writeBatch(this.firestore);

    // If specific combinations are provided, delete only those
    if (modelIds?.length && promptIds?.length) {
      for (const modelId of modelIds) {
        for (const promptId of promptIds) {
          await this.deletePromptRatingForCombination(batch, modelId, promptId, user.uid);
        }
      }
    } else {
      // Otherwise, find all user's prompt ratings and delete them
      const userPromptRatings = await this.getRatedPrompts();
      if (userPromptRatings.length === 0) return;

      for (const { modelId, promptId } of userPromptRatings) {
        await this.deletePromptRatingForCombination(batch, modelId, promptId, user.uid);
      }
    }

    return batch.commit();
  }

  /**
   * Get all model-prompt combinations rated by the current user
   * @param minRating Optional minimum rating to filter by
   */
  async getRatedPrompts(minRating = 0): Promise<Array<{ modelId: string; promptId: string; rating: number }>> {
    const userId = this.auth.currentUser?.uid;
    if (!userId) return [];

    // Query all prompt-ratings collections where the user has rated
    const promptRatingsQuery = query(
      firestoreCollection(this.firestore, this.PROMPT_RATINGS_COLLECTION)
    );

    const querySnapshot = await getDocs(promptRatingsQuery);
    const results: Array<{ modelId: string; promptId: string; rating: number }> = [];

    for (const docRef of querySnapshot.docs) {
      const userRatingRef = doc(
        this.firestore,
        `${this.PROMPT_RATINGS_COLLECTION}/${docRef.id}/ratings/${userId}`
      );
      const ratingDoc = await getDoc(userRatingRef);

      if (ratingDoc.exists()) {
        const rating = ratingDoc.data()?.['rating'];
        const modelId = ratingDoc.data()?.['modelId'];
        const promptId = ratingDoc.data()?.['promptId'];

        if (rating >= minRating && modelId && promptId) {
          results.push({
            modelId,
            promptId,
            rating
          });
        }
      }
    }

    return results;
  }

  /**
   * Helper method to delete a prompt rating and update stats
   */
  private async deletePromptRatingForCombination(batch: any, modelId: string, promptId: string, userId: string): Promise<void> {
    const promptRatingKey = `${modelId}-${promptId}`;
    const userRatingRef = doc(
      this.firestore,
      `${this.PROMPT_RATINGS_COLLECTION}/${promptRatingKey}/ratings/${userId}`
    );

    // Get the user's current rating first
    const userRatingDoc = await getDoc(userRatingRef);
    if (!userRatingDoc.exists()) return;

    const userRating = userRatingDoc.data()?.['rating'] || 0;

    // Delete the user's rating
    batch.delete(userRatingRef);

    // Get prompt reference to update its stats
    const promptRef = doc(this.firestore, `${this.PROMPT_RATINGS_COLLECTION}/${promptRatingKey}`);
    const promptDoc = await getDoc(promptRef);

    if (!promptDoc.exists()) return;

    const currentStats = promptDoc.data();
    const currentTotal = currentStats['totalRatings'] || 0;
    const currentAverage = currentStats['averageRating'] || 0;

    // If this was the only rating, delete the prompt document
    if (currentTotal <= 1) {
      batch.delete(promptRef);
      return;
    }

    // Recalculate average without the deleted rating
    const currentSum = currentAverage * currentTotal;
    const newTotal = currentTotal - 1;
    const newAverage = newTotal > 0 ? (currentSum - userRating) / newTotal : 0;

    // Update prompt stats
    batch.set(promptRef, {
      averageRating: parseFloat(newAverage.toFixed(2)),
      totalRatings: newTotal,
      lastUpdated: serverTimestamp(),
      modelId,
      promptId
    }, { merge: true });
  }

  // ===== MODEL COMMENT METHODS =====

  /**
   * Get comment for a model
   * @param modelId The ID of the model to get comment for
   */
  getModelComment(modelId: string): Observable<ModelComment | null> {
    return new Observable(subscriber => {
      const commentRef = doc(this.firestore, `${this.MODEL_COMMENTS_COLLECTION}/${modelId}`);

      getDoc(commentRef).then(commentDoc => {
        if (commentDoc.exists()) {
          const data = commentDoc.data();
          const comment: ModelComment = {
            modelId,
            comment: data['comment'] || '',
            lastUpdated: data['lastUpdated'],
            lastUpdatedBy: data['lastUpdatedBy'] || ''
          };
          subscriber.next(comment);
        } else {
          subscriber.next(null);
        }
        subscriber.complete();
      }).catch(error => {
        console.error('Error getting model comment:', error);
        subscriber.error(error);
      });
    });
  }

  /**
   * Save or update a comment for a model
   * @param modelId The ID of the model
   * @param comment The comment text
   */
  async saveModelComment(modelId: string, comment: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || !user.email) {
      throw new Error('User must be authenticated to save comments');
    }

    const commentRef = doc(this.firestore, `${this.MODEL_COMMENTS_COLLECTION}/${modelId}`);

    const modelComment: ModelComment = {
      modelId,
      comment: comment.trim(),
      lastUpdated: serverTimestamp(),
      lastUpdatedBy: user.email
    };

    await setDoc(commentRef, modelComment, { merge: true });
  }

  /**
   * Delete comment for a model
   * @param modelId The ID of the model
   */
  async deleteModelComment(modelId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to delete comments');
    }

    const commentRef = doc(this.firestore, `${this.MODEL_COMMENTS_COLLECTION}/${modelId}`);
    await setDoc(commentRef, {
      modelId,
      comment: '',
      lastUpdated: serverTimestamp(),
      lastUpdatedBy: user.email || 'unknown'
    }, { merge: true });
  }
}
